import { FrequencyProvider } from '@monorepo/shared/contexts/FrequencyContext';
import { CUSTOM_FREQUENCY } from '@monorepo/shared/contexts/FrequencyContext/types';
import { SaveState } from '@monorepo/shared/types/SaveState';
import _get from 'lodash.get';
import { IntervalFrequencyEnum, UTCEquivalentOfLocal } from 'mapistry-shared';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  createCalendarFrequencyAction,
  deleteCalendarFrequencyAction,
  fetchCalendarFrequenciesAction,
  resetCalendarErrorStateAction,
} from '../../../../actions/calendar';
import APP from '../../../../config';
import { withFeatureFlags } from '../../../../contexts/withFeatureFlags';
import { delayedModalClose, isNullOrUndefined } from '../../../../utils';
import withProvider from '../../../withProvider';
import { InspectionFrequencyModal as InspectionFrequencyModalView } from './InspectionFrequencyModal';

class InspectionFrequencyModalContainer extends Component {
  constructor(props) {
    super(props);
    this.handleClose = this.handleClose.bind(this);
    this.handleDeleteFrequency = this.handleDeleteFrequency.bind(this);
    this.handleFrequencyChange = this.handleFrequencyChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.state = {
      customFrequency: null,
      idsToDelete: [],
      saveState: SaveState.CLEAN,
      selectedFrequency: null,
      selectedStartDate: null,
    };
  }

  componentDidMount() {
    const { calendarName, fetchFrequencies, projectId } = this.props;

    fetchFrequencies(projectId, calendarName);
  }

  componentDidUpdate(prevProps) {
    const { open } = this.props;

    if (open && !prevProps.open) {
      this.setState({
        customFrequency: null,
        idsToDelete: [],
        saveState: SaveState.CLEAN,
        selectedFrequency: null,
        selectedStartDate: null,
      });
    }
  }

  handleClose() {
    const { onClose, resetErrorState } = this.props;
    resetErrorState();
    onClose();
  }

  // block app nav?
  handleDeleteFrequency(id) {
    const { idsToDelete } = this.state;
    this.setState({
      idsToDelete: [...idsToDelete, id],
      saveState: SaveState.DIRTY,
    });
  }

  handleFrequencyChange(
    updatedFrequency,
    updatedCustomFrequency,
    updatedStartDate,
  ) {
    const { idsToDelete } = this.state;

    const saveState =
      isNullOrUndefined(updatedFrequency) && !idsToDelete.length
        ? SaveState.CLEAN
        : SaveState.DIRTY;

    this.setState({
      saveState,
      selectedFrequency: updatedFrequency,
      customFrequency: updatedCustomFrequency,
      selectedStartDate: updatedStartDate,
    });
  }

  async handleSave() {
    const { reloadEvents } = this.props;

    this.setState({ saveState: SaveState.SAVING });

    const didSave = await this.saveFrequencyChanges();
    if (!didSave) {
      this.setState({ saveState: SaveState.DIRTY });
      return;
    }

    reloadEvents();
    this.setState({ saveState: SaveState.SAVED }, () =>
      delayedModalClose(this.handleClose),
    );
  }

  /**
   * Applies the deletions, then the new frequency, waiting on every request. The
   * calendar may only be reloaded once the whole history has landed: events built from
   * a half-applied history cover overlapping periods and can come back with an interval
   * that doesn't hold the date on screen, which the card answers by fetching again.
   */
  async saveFrequencyChanges() {
    const { calendarName, createFrequency, deleteFrequency, projectId } =
      this.props;
    const {
      customFrequency,
      idsToDelete,
      selectedFrequency,
      selectedStartDate,
    } = this.state;

    const deletions = await Promise.allSettled(
      idsToDelete.map((id) => deleteFrequency(projectId, calendarName, id)),
    );
    if (deletions.some(({ status }) => status === 'rejected')) {
      return false;
    }

    if (!selectedFrequency || !selectedStartDate) {
      return true;
    }

    return createFrequency(
      projectId,
      calendarName,
      selectedFrequency,
      UTCEquivalentOfLocal(selectedStartDate),
      customFrequency,
    ).then(
      () => true,
      () => false,
    );
  }

  frequencyOptions() {
    const frequencies = this.frequenciesWithDeletions();
    const IntervalFrequencyEnumGroup = [CUSTOM_FREQUENCY];
    const options = [
      IntervalFrequencyEnum.NEVER,
      IntervalFrequencyEnum.AS_NEEDED,
      IntervalFrequencyEnum.DAY,
      IntervalFrequencyEnum.WEEK,
      IntervalFrequencyEnum.MONTH,
      IntervalFrequencyEnum.QUARTER,
      IntervalFrequencyEnum.BIANNUAL,
      IntervalFrequencyEnum.YEAR,
      IntervalFrequencyEnumGroup,
    ];

    const latestFrequencyModel = frequencies[frequencies.length - 1];
    if (_get(latestFrequencyModel, 'customFrequency')) {
      return options;
    }
    const latestFrequency = _get(latestFrequencyModel, 'frequency');
    return options.filter((o) => o !== latestFrequency);
  }

  frequenciesWithDeletions() {
    const { frequencies } = this.props;
    const { idsToDelete } = this.state;
    return frequencies.filter((f) => !idsToDelete.includes(f.id));
  }

  render() {
    const { customFrequency, saveState, selectedFrequency, selectedStartDate } =
      this.state;
    const { featureFlags, open, serverErrorMessage, title } = this.props;

    const frequencies = this.frequenciesWithDeletions();

    return (
      <FrequencyProvider
        allowMultiDayCustomDailyFrequency={
          !!featureFlags?.isMultiDayCustomDailyFrequencyEnabled
        }
        customFrequency={customFrequency}
        frequencies={frequencies}
        onFrequencyChange={this.handleFrequencyChange}
        onSave={this.handleSave}
        saveState={saveState}
        selectedFrequency={selectedFrequency}
        selectedStartDate={selectedStartDate}
      >
        <InspectionFrequencyModalView
          errors={
            serverErrorMessage
              ? ['There was a problem saving. Please try again later.']
              : null
          }
          frequencies={frequencies}
          frequencyOptions={this.frequencyOptions()}
          onClose={this.handleClose}
          onDeleteFrequency={this.handleDeleteFrequency}
          onSave={this.handleSave}
          open={open}
          saveState={saveState}
          title={title}
        />
      </FrequencyProvider>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { calendar } = state;
  return {
    frequencies: _get(calendar.frequencies, ownProps.calendarName, []),
    projectId: APP.projectId,
    serverErrorMessage: _get(calendar.errorMessage, 'message'),
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchFrequencies: (projectId, calendarName) =>
    dispatch(fetchCalendarFrequenciesAction(projectId, calendarName)),
  deleteFrequency: (projectId, calendarName, id) =>
    dispatch(deleteCalendarFrequencyAction(projectId, calendarName, id)),
  createFrequency: (
    projectId,
    calendarName,
    newFrequency,
    newStartDate,
    customFrequency,
  ) =>
    dispatch(
      createCalendarFrequencyAction(
        projectId,
        calendarName,
        newFrequency,
        newStartDate,
        customFrequency,
      ),
    ),
  resetErrorState: () => dispatch(resetCalendarErrorStateAction()),
});

InspectionFrequencyModalContainer.propTypes = {
  featureFlags: PropTypes.shape({
    isMultiDayCustomDailyFrequencyEnabled: PropTypes.bool,
  }),
  calendarName: PropTypes.string.isRequired,
  createFrequency: PropTypes.func.isRequired,
  deleteFrequency: PropTypes.func.isRequired,
  serverErrorMessage: PropTypes.string,
  frequencies: PropTypes.arrayOf(
    PropTypes.shape({
      frequency: PropTypes.string,
      numberOfInspections: PropTypes.number,
      startDate: PropTypes.string,
    }),
  ),
  fetchFrequencies: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
  reloadEvents: PropTypes.func.isRequired,
  resetErrorState: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

InspectionFrequencyModalContainer.defaultProps = {
  featureFlags: {},
  serverErrorMessage: null,
  frequencies: [],
  open: false,
};

const InspectionFrequencyModal = withProvider(
  withFeatureFlags(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(InspectionFrequencyModalContainer),
  ),
);

export { InspectionFrequencyModal };
